import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useSystem } from '../context/SystemContext';

// Game-system switcher. Renders nothing until at least two systems are enabled
// (so it stays invisible while only D&D 3.5e is live). Mirrors the header nav
// dropdowns (nav-link trigger + dark-stone dropdown-menu) so it inherits the
// same chrome theming in light and dark mode. Changing it reloads under the
// new system.
//
// Touch ergonomics: hover-open is gated behind (hover: hover) — on touch
// devices the synthesized mouseenter fires before click and would immediately
// re-toggle the menu closed. Touch relies on click, plus the outside-click /
// Escape close handlers (same pattern as SearchAutocomplete).
const canHover = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover)').matches;

const SHORT_NAMES = { 'dnd-3.5': '3.5e', 'pf-1e': 'PF1e' };

const SystemSelector = () => {
  const { system, systems, setSystem } = useSystem();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  // Hovering opens the menu, and a mouse that hovers then clicks: that click
  // must confirm the menu hovering opened, never close it again (the header's
  // two menus work the same way). Without this, a click on the ruleset name
  // with a mouse did nothing visible.
  const hoverOpened = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!systems || systems.length < 2) return null;

  const current = systems.find((s) => s.slug === system);

  return (
    <div
      className="dropdown"
      ref={wrapperRef}
      onMouseEnter={() => {
        if (!canHover()) return;
        if (!open) hoverOpened.current = true;
        setOpen(true);
      }}
      onMouseLeave={() => {
        if (!canHover()) return;
        hoverOpened.current = false;
        setOpen(false);
      }}
    >
      <button
        type="button"
        className="nav-link nav-link-inactive"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Game system"
        onClick={() => {
          if (hoverOpened.current) {
            hoverOpened.current = false;
            setOpen(true);
            return;
          }
          setOpen((o) => !o);
        }}
      >
        {/* Narrow phones: icon + chevron only — the name doesn't fit the top bar */}
        {/* The short name (decision #1442): the full one took the room the header's words need at 1280 px; the menu names it in full. */}
        <span className="hidden sm:inline" title={current?.name || system}>{SHORT_NAMES[system] || current?.name || system}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="dropdown-menu" role="menu" style={{ left: 'auto', right: 0, minWidth: '11rem' }}>
          {systems.map((s) => {
            const active = s.slug === system;
            return (
              <button
                key={s.slug}
                type="button"
                role="menuitem"
                className={`dropdown-item w-full text-left ${active ? 'dropdown-item-active' : 'dropdown-item-inactive'}`}
                onClick={() => { setOpen(false); setSystem(s.slug); }}
              >
                <Check className="w-4 h-4 flex-shrink-0" style={{ opacity: active ? 1 : 0 }} />
                <span>{s.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SystemSelector;
